import type { ReactElement } from "react";
import { Link, useParams, useSearchParams } from "react-router";

import { useApiData } from "../../api/use-api-data.js";
import { queryValue } from "../../app/navigation.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { ApiState, titleFor } from "../../components/api-state.js";
import { adminCenterUrls } from "../admin-center/urls.js";

import { invoiceApi, invoiceEntryOf, invoiceUrls } from "./urls.js";

/** 明細 1 行（サーバの ApiInvoiceLine と同じ欄）。 */
export interface InvoiceLine {
    readonly description: string;
    readonly unitPriceLabel: string;
    readonly qty: number;
    readonly amountLabel: string;
}

/** 請求書 1 通（<c>GET /api/tenants/{tenantId}/invoices/{id}</c>。サーバの ApiInvoiceResponse と同じ欄）。 */
export interface Invoice {
    readonly tenantId: number;
    readonly pageTitle: string;
    readonly invoiceNumber: string;
    readonly invoiceDateLabel: string;
    readonly dueDateLabel: string;
    readonly periodLabel: string;
    readonly company: string;
    readonly title: string;
    readonly planName: string;
    readonly paymentMethodLabel: string;
    readonly totalLabel: string;
    readonly lines: readonly InvoiceLine[];
    readonly taxRateLabel: string;
    readonly subtotalLabel: string;
    readonly taxLabel: string;
    readonly totalAmountLabel: string;
    readonly issuerName: string;
    /** 発行元の郵便番号（「〒」は付いていない）・住所・連絡先。<b>空文字ならその行を出さない。</b> */
    readonly issuerPostalCode: string;
    readonly issuerAddress: string;
    readonly issuerTel: string;
    readonly registrationNumber: string | null;
    /** お振込先の行。<b>請求書払いのときだけ</b>入っていて、空なら区画ごと出さない。 */
    readonly bankAccountLines: readonly string[];
    /** 「備考」の行（請求書払いは振込の注意、カード払いは自動決済の案内）。空なら区画ごと出さない。 */
    readonly notes: readonly string[];
}

/**
 * 請求書 1 通。<b>印刷する画面</b>。
 *
 * <b>印刷ボタンは onClick で持つ。</b><c>data-print</c>（ClientApp/forms/print-button.ts）は
 * 読み込んだ時点のボタンにしか付かず、React が後から描いたボタンを拾えない。
 * 紙に要らないもの（ヘッダー・フッター・通知）は print.css が落とし、ボタンは <c>print:hidden</c>。
 */
export function InvoicePage(): ReactElement | null {
    const [search] = useSearchParams();
    // 今のアプリが作るリンクは /TenantInvoice/Show/202601?tenantId=1（id がパスに載る）
    const { id: pathId } = useParams();
    const id = pathId ?? queryValue(search, "id") ?? "";
    const data = useApiData<Invoice>(invoiceApi.show(queryValue(search, "tenantId") ?? "", id));
    usePageMeta(titleFor(data, data.state === "ready" ? data.value.pageTitle : "請求書"));

    if (data.state !== "ready") {
        return <ApiState data={data} what="請求書" />;
    }
    const invoice = data.value;
    // 管理センターの請求書の一覧から開いたときは、管理センターの契約の画面へ戻す。
    // 請求書のメニューから開いた一覧から来たときは、その一覧（メニューへ戻れる一覧）へ戻す
    const entry = invoiceEntryOf(queryValue(search, "from"));
    const back = entry === "center"
        ? { to: adminCenterUrls.tenant(invoice.tenantId), label: "管理センターの契約へ戻る" }
        : {
            to: entry === "menu" ? invoiceUrls.listFromMenu(invoice.tenantId) : invoiceUrls.list(invoice.tenantId),
            label: "請求書一覧へ戻る",
        };

    return (
        <div className="page max-w-3xl">
            <div className="mb-4 flex items-center justify-between print:hidden">
                <Link to={back.to} className="btn-secondary btn-sm">{back.label}</Link>
                <button type="button" className="btn-secondary btn-sm" onClick={() => { globalThis.print(); }}>
                    印刷する
                </button>
            </div>

            <div className="panel print-sheet">
                <div className="mb-8 flex flex-wrap items-start justify-between gap-4 print:mb-5">
                    <h1 className="page-title">請求書</h1>
                    <dl className="grid min-w-64 grid-cols-[7rem_1fr] gap-y-1 text-sm">
                        <dt className="text-slate-500">請求書番号</dt><dd>{invoice.invoiceNumber}</dd>
                        <dt className="text-slate-500">請求日</dt><dd>{invoice.invoiceDateLabel}</dd>
                        <dt className="text-slate-500">お支払期限</dt><dd>{invoice.dueDateLabel}</dd>
                        {/* 適格請求書の「取引年月日」。月額なので日には定まらず期間で示す */}
                        <dt className="text-slate-500">対象期間</dt><dd>{invoice.periodLabel}</dd>
                    </dl>
                </div>

                <div className="mb-8 print:mb-5">
                    <div className="border-b border-slate-300 pb-2 text-lg font-semibold">{invoice.company} 御中</div>
                    <div className="mt-1 text-sm text-slate-500">
                        {invoice.title}（{invoice.planName} / {invoice.paymentMethodLabel}）
                    </div>
                </div>

                <div className="mb-8 print:mb-5">
                    <span className="text-sm text-slate-500">ご請求金額（税込）</span>
                    <div className="text-2xl font-bold">{invoice.totalLabel}</div>
                </div>

                <InvoiceTable invoice={invoice} />

                {/* 発行元の右にお振込先と備考。縦に積むと、明細が多いときに紙の 2 枚目へこぼれる（明細 12 行まで A4 縦 1 枚） */}
                <div className="grid gap-4 sm:grid-cols-2 print:grid-cols-2">
                    <InvoiceIssuer issuer={invoice} />
                    <InvoicePaymentGuide invoice={invoice} />
                </div>
            </div>
        </div>
    );
}

/** 明細と合計の欄（請求書と見積書で同じ。サーバの ApiInvoiceResponse・ApiEstimateResponse の同じ名前の欄）。 */
export type InvoiceTotals = Pick<Invoice, "lines" | "taxRateLabel" | "subtotalLabel" | "taxLabel" | "totalAmountLabel">;

/** 発行元の欄（請求書と見積書で同じ）。 */
export type InvoiceIssuerFields = Pick<
    Invoice, "issuerName" | "issuerPostalCode" | "issuerAddress" | "issuerTel" | "registrationNumber"
>;

/** 発行元。**登録番号（T 番号）は適格請求書の要件**——無いと受け取った側が控除できない。<b>見積書も同じものを出す</b>。 */
export function InvoiceIssuer({ issuer }: { readonly issuer: InvoiceIssuerFields }): ReactElement {
    return (
        <div className="text-sm text-slate-500">
            <p>{issuer.issuerName}</p>
            {issuer.issuerPostalCode === "" ? null : <p>〒{issuer.issuerPostalCode}</p>}
            {issuer.issuerAddress === "" ? null : <p>{issuer.issuerAddress}</p>}
            {issuer.issuerTel === "" ? null : <p>連絡先：{issuer.issuerTel}</p>}
            {/* 連絡先との間を 1 行空ける。登録番号が無ければ見出しごと出さない */}
            {issuer.registrationNumber === null ? null : (
                <div className="mt-5">
                    <p>適格請求書発行事業者登録番号</p>
                    <p className="font-mono">{issuer.registrationNumber}</p>
                </div>
            )}
        </div>
    );
}

/**
 * お振込先と備考（旧ポータルの請求書と同じく、お振込先 → 備考の順）。<b>出し分けと文はサーバが決める</b>——
 * 請求書払いはお振込先と振込の注意、カード払いは自動決済の案内だけが届く。
 */
function InvoicePaymentGuide(
    { invoice }: { readonly invoice: Pick<Invoice, "bankAccountLines" | "notes"> }): ReactElement | null {
    if (invoice.bankAccountLines.length === 0 && invoice.notes.length === 0) {
        return null;
    }
    return (
        <div className="space-y-5 text-sm print:space-y-3">
            {invoice.bankAccountLines.length === 0 ? null : (
                <div data-invoice-bank-account="">
                    <p className="font-semibold">お振込先</p>
                    {invoice.bankAccountLines.map((line) => <p key={line}>{line}</p>)}
                </div>
            )}
            {invoice.notes.length === 0 ? null : (
                <div data-invoice-notes="">
                    <p className="font-semibold">備考</p>
                    <ul className="list-disc pl-5">
                        {invoice.notes.map((note) => <li key={note}>{note}</li>)}
                    </ul>
                </div>
            )}
        </div>
    );
}

/** 明細と合計。<b>税率は 1 つの値から出す</b>（区分の見出しと税額の見出しを食い違わせない）。見積書も同じ表。 */
export function InvoiceTable({ invoice }: { readonly invoice: InvoiceTotals }): ReactElement {
    return (
        <table className="mb-8 w-full text-sm print:mb-5">
            <thead>
                <tr className="border-b border-slate-200 text-left text-slate-500">
                    <th scope="col" className="py-2 font-medium">摘要</th>
                    <th scope="col" className="py-2 text-right font-medium">単価</th>
                    <th scope="col" className="py-2 text-right font-medium">数量</th>
                    <th scope="col" className="py-2 text-right font-medium">金額</th>
                </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
                {invoice.lines.map((line, index) => (
                    // 同じ摘要が並ぶことがある。明細は並べ替えないので位置で見分ける
                    <tr key={`${String(index)}:${line.description}`}>
                        <td className="py-2">{line.description}</td>
                        <td className="py-2 text-right">{line.unitPriceLabel}</td>
                        <td className="py-2 text-right">{line.qty}</td>
                        <td className="py-2 text-right">{line.amountLabel}</td>
                    </tr>
                ))}
            </tbody>
            <tfoot className="border-t border-slate-200">
                <tr>
                    <td colSpan={3} className="py-2 text-right text-slate-500">
                        {invoice.taxRateLabel}% 対象 小計（税抜）
                    </td>
                    <td className="py-2 text-right">{invoice.subtotalLabel}</td>
                </tr>
                <tr>
                    <td colSpan={3} className="py-2 text-right text-slate-500">
                        消費税（{invoice.taxRateLabel}%）
                    </td>
                    <td className="py-2 text-right">{invoice.taxLabel}</td>
                </tr>
                <tr>
                    <td colSpan={3} className="py-2 text-right font-semibold">合計</td>
                    <td className="py-2 text-right font-semibold">{invoice.totalAmountLabel}</td>
                </tr>
            </tfoot>
        </table>
    );
}
